import React from 'react';
import { Tag } from 'primereact/tag';

/**
 * Como se ve una alerta: colores, severidad y formato de los codigos.
 *
 * Vive en un solo archivo y no repetido en cada pantalla, por la misma razon
 * por la que el transportista salio del frontend a la vista: una regla copiada
 * en tres lugares es una regla que va a divergir.
 *
 * La criticidad es LA MISMA que usa el tablero de alarmas GPS
 * (DashboardGpsCorteService.esCritico). Si los dos tableros marcaran cosas
 * distintas como graves, el color dejaria de significar algo.
 */

const may = (s) => (s || '').toString().toUpperCase();

/**
 * Un evento grave es el que exige mirar ya: panico, corte de cable, tarjeta no
 * autorizada, exceso de velocidad, desvio, tapa abierta.
 */
export const esCritico = (evento) => {
    const e = may(evento);
    return e.includes('PANICO') || e.includes('CORTE') || e.includes('NO AUTORIZ')
        || e.includes('VELOCID') || e.includes('DESVIO') || e.includes('TAPA');
};

/** Candado o vehicular, igual que en el tablero GPS. */
export const familia = (evento, tipo) => {
    const t = may(tipo);
    const e = may(evento);
    if (t.includes('CANDADO') || e.includes('CABLE') || e.includes('TARJETA')
        || e.includes('CANDADO') || e.includes('APERTURA') || e.includes('TAPA')) {
        return 'Candado';
    }
    return 'Vehicular';
};

/** El color del tipo de evento. Cada tipo, siempre el mismo. */
const severidadTipo = (tipo) => {
    switch (may(tipo)) {
        case 'CANDADO': return 'danger';
        case 'PARADA': return 'warning';
        case 'BITACORA': return 'info';
        case 'SERVICIO_VIP': return 'success';
        default: return null;
    }
};

/** Etiqueta del tipo. */
export const TagTipo = ({ tipo }) => {
    if (!tipo) return <span className="text-color-secondary">—</span>;
    return <Tag value={tipo} severity={severidadTipo(tipo)} />;
};

/**
 * Nivel de un evento. Tres escalones, no doce colores.
 *
 * El tipo dice CANDADO y no alcanza: adentro conviven un cable insertado, que
 * es rutina, y un corte de cable, que hay que mirar ya. Medido sobre siete dias
 * de produccion, CANDADO son 14.024 alertas repartidas en ocho eventos.
 *
 *   critico  -- exige mirar ya: panico, corte de cable, tarjeta no autorizada,
 *               tapa abierta, exceso de velocidad, desvio.
 *   atencion -- puede ser normal o no, hay que leerlo: cable desconectado
 *               (6.547 en 7 dias), apertura remota.
 *   rutina   -- el curso normal: cable insertado, tarjeta autorizada o
 *               asignada, parada varias.
 */
export const nivelEvento = (evento) => {
    const e = may(evento);
    // "(sin evento)" no es rutina: es un dato que falta, y pintarlo de verde lo
    // haria pasar por normal.
    if (!e || e.startsWith('(')) return 'ninguno';
    if (esCritico(evento)) return 'critico';
    if (e.includes('DESCONECT') || e.includes('APERTURA') || e.includes('ALIMENTACION')
        || e.includes('MECANICA')) {
        return 'atencion';
    }
    return 'rutina';
};

/** El color de cada nivel, para reusarlo en la grilla y en el tablero. */
export const COLOR_NIVEL = {
    critico: 'var(--red-500)',
    atencion: 'var(--orange-500)',
    rutina: 'var(--teal-500)',
    ninguno: 'var(--surface-400)'
};

/**
 * El evento.
 *
 * Solo el critico lleva etiqueta llena. Los otros van con un punto de color al
 * costado: alcanza para distinguirlos de un vistazo sin convertir la grilla en
 * un semaforo permanente, que es lo que hace que el operador deje de mirar el
 * color.
 */
export const TagEvento = ({ evento }) => {
    if (!evento) return <span className="text-color-secondary">—</span>;
    const nivel = nivelEvento(evento);
    if (nivel === 'critico') {
        return (
            <Tag severity="danger" icon="pi pi-exclamation-triangle" value={evento}
                 title="Evento crítico: requiere atención inmediata" />
        );
    }
    return (
        <span className="inline-flex align-items-center gap-2"
              title={nivel === 'atencion' ? 'Requiere lectura' : 'Curso normal'}>
            <span style={{
                width: 8, height: 8, borderRadius: '50%', flex: '0 0 8px',
                background: COLOR_NIVEL[nivel]
            }} />
            <span style={nivel === 'atencion' ? { fontWeight: 600 } : undefined}>{evento}</span>
        </span>
    );
};

/** Estado de atencion de la alerta. */
export const TagEstado = ({ estado }) => {
    const e = may(estado);
    if (!e) return <span className="text-color-secondary">—</span>;
    if (e.includes('ATEND')) return <Tag value={estado} severity="success" />;
    if (e.includes('PEND') || e.includes('SIN')) return <Tag value={estado} severity="warning" />;
    return <Tag value={estado} />;
};

/**
 * Un codigo: placa, candado, booking o contenedor.
 *
 * En monoespaciada porque se comparan caracter por caracter. CAAU4269050 y
 * CAAU4269056 en tipografia normal se leen iguales.
 */
export const Codigo = ({ valor }) => {
    if (!valor) return <span className="text-color-secondary">—</span>;
    return (
        <span style={{
            fontFamily: 'ui-monospace, Consolas, "Courier New", monospace',
            fontSize: '.92em', letterSpacing: '.01em'
        }}>{valor}</span>
    );
};

/**
 * El transportista, avisando cuando NO salio de la bitacora.
 *
 * En ese caso el nombre puede ser una bolsa como "DOLE CANDADOS", que agrupa
 * cientos de dispositivos, y no un transportista real.
 */
export const Transportista = ({ nombre, origen }) => {
    if (!nombre) return <span className="text-color-secondary">—</span>;
    const dudoso = origen && origen !== 'BITACORA';
    return (
        <span title={dudoso ? 'Sale de la empresa del viaje, no de la bitácora' : undefined}>
            {nombre}
            {dudoso && <i className="pi pi-info-circle ml-2 text-color-secondary"
                          style={{ fontSize: '.75rem' }} />}
        </span>
    );
};

/** Color del borde izquierdo de una tarjeta, segun lo que pide atencion. */
export const bordeTarjeta = (a) => {
    if (esCritico(a.novedad) || esCritico(a.evento)) return 'var(--red-500)';
    if (a.leida === false) return 'var(--primary-color)';
    if (a.requiereConfirmacion && !a.revisada) return 'var(--orange-500)';
    return 'var(--surface-border)';
};
